import { useRef, useEffect, useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { workOrdersApi, tecniciApi, committentiApi } from '../services/api'
import { AppuntamentoChip } from '../components/AppuntamentoChip'
import { useAuth } from '../contexts/AuthContext'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { MultiSelectDropdown } from '../components/MultiSelectDropdown'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

const STATO_COLORI: Record<string, string> = {
  ricevuto: '#3b82f6', assegnato: '#f59e0b',
  sospeso: '#6b7280', eseguito: '#22c55e', annullato: '#ef4444',
}
const STATO_LABEL: Record<string, string> = {
  ricevuto: 'Ricevuto', assegnato: 'Assegnato',
  sospeso: 'Sospeso', eseguito: 'Eseguito', annullato: 'Annullato',
}

function creaIcona(stato: string, selected = false): L.DivIcon {
  const c = STATO_COLORI[stato] ?? '#6b7280'
  const s = selected ? 36 : 28
  return L.divIcon({
    html: `<svg width="${s}" height="${s}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="13" r="10" fill="${c}" stroke="white" stroke-width="${selected ? 3 : 2}" filter="drop-shadow(0 2px 4px rgba(0,0,0,.35))"/><polygon points="16,28 10,18 22,18" fill="${c}" stroke="white" stroke-width="1"/>${selected ? '<circle cx="16" cy="13" r="4" fill="white"/>' : ''}</svg>`,
    className: '', iconSize: [s, s], iconAnchor: [s / 2, s], popupAnchor: [0, -s],
  })
}

function creaIconaNumero(n: number, stato: string, selected = false, ringColor?: string): L.DivIcon {
  const c = STATO_COLORI[stato] ?? '#6b7280'
  const inner = selected ? 34 : 28
  const ring = ringColor ? 4 : 0
  const total = inner + ring * 2
  const html = ringColor
    ? `<div style="
        background:${ringColor}; border-radius:50%;
        width:${total}px; height:${total}px; padding:${ring}px;
        box-shadow:0 2px 8px ${ringColor}88,0 2px 4px rgba(0,0,0,.3);
        box-sizing:border-box;
      "><div style="
        background:${c}; color:white; border-radius:50%;
        width:${inner}px; height:${inner}px; display:flex; align-items:center;
        justify-content:center; font-weight:800; font-size:${n > 9 ? 11 : 13}px;
        border:2px solid white; font-family:system-ui,sans-serif;
      ">${n}</div></div>`
    : `<div style="
        background:${c}; color:white; border-radius:50%;
        width:${inner}px; height:${inner}px; display:flex; align-items:center;
        justify-content:center; font-weight:800; font-size:${n > 9 ? 11 : 13}px;
        border:${selected ? 3 : 2}px solid white;
        box-shadow:0 2px 6px rgba(0,0,0,.4); font-family:system-ui,sans-serif;
      ">${n}</div>`
  return L.divIcon({
    html, className: '', iconSize: [total, total], iconAnchor: [total / 2, total / 2],
  })
}

function slotToKey(s?: string | null): string {
  if (!s) return '99:99'
  const u = s.trim().toUpperCase()
  if (u === 'AM') return '00:00'
  if (u === 'PM') return '13:00'
  const m = s.match(/^(\d{2}:\d{2})/)
  return m ? m[1] : '99:99'
}

export default function Mappa() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const qc = useQueryClient()
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersLayerRef = useRef<L.LayerGroup | null>(null)
  const polylineRef = useRef<L.Polyline | null>(null)

  const [statoFiltri, setStatoFiltri] = useState<string[]>([])
  const [filtroPriorita, setFiltroPriorita] = useState('')
  const [tecnicoFiltri, setTecnicoFiltri] = useState<string[]>([])
  const [filtroCommittente, setFiltroCommittente] = useState('')
  const [filtroRegione, setFiltroRegione] = useState('')
  const [filtroLotto, setFiltroLotto] = useState('')
  const [filtroDataDa, setFiltroDataDa] = useState('')
  const [filtroDataA, setFiltroDataA] = useState('')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [sidebarWO, setSidebarWO] = useState<any | null>(null)

  // Giro mode
  const [modalitaGiro, setModalitaGiro] = useState(false)
  const [ordineGiro, setOrdineGiro] = useState<any[]>([])
  const [showAggiungiModal, setShowAggiungiModal] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mappa-pratiche', filtroCommittente, filtroDataDa, filtroDataA],
    queryFn: () => workOrdersApi.list({
      per_page: 2000,
      ...(filtroCommittente && { committente_id: Number(filtroCommittente) }),
      ...(filtroDataDa && { data_da: filtroDataDa }),
      ...(filtroDataA && { data_a: filtroDataA }),
    }).then(r => r.data),
    staleTime: 60_000,
  })

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-mappa'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  const { data: committentiData } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  const salvaOrdineMut = useMutation({
    mutationFn: (ordini: { wo_id: number; ordine: number }[]) =>
      workOrdersApi.salvaOrdineGiro({ ordini }),
    onSuccess: () => {
      toast.success(`Ordine giro salvato (${ordineGiro.length} siti)`)
      qc.invalidateQueries({ queryKey: ['mappa-pratiche'] })
    },
    onError: () => toast.error('Errore salvataggio ordine'),
  })

  const committenti: any[] = committentiData ?? []
  const tutti: any[] = data?.items ?? []

  const regioniDisponibili = [...new Set(tutti.map((w: any) => w.cliente_regione).filter(Boolean))].sort() as string[]
  const lottiDisponibili = [...new Set(tutti.map((w: any) => w.lotto).filter(Boolean))].sort() as string[]

  const pratiche = tutti.filter(wo => {
    if (!wo.cliente_lat || !wo.cliente_lng) return false
    if (statoFiltri.length && !statoFiltri.includes(wo.stato)) return false
    if (tecnicoFiltri.length && !tecnicoFiltri.includes(String(wo.tecnico_id))) return false
    if (filtroRegione && wo.cliente_regione !== filtroRegione) return false
    if (filtroLotto && (wo.lotto ?? '') !== filtroLotto) return false
    if (filtroPriorita && wo.priorita !== filtroPriorita) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (
      wo.numero_progressivo?.toLowerCase().includes(q) ||
      wo.cliente_nome?.toLowerCase().includes(q) ||
      wo.cliente_comune?.toLowerCase().includes(q) ||
      wo.committente_nome?.toLowerCase().includes(q)
    )
  })

  const PRIORITA_COLORI: Record<string, string> = {
    critica: '#dc2626', urgente: '#F97316', alta: '#3b82f6', normale: '#6b7280',
  }
  const conteggioP = pratiche.reduce((acc, wo) => {
    if (wo.priorita) acc[wo.priorita] = (acc[wo.priorita] || 0) + 1; return acc
  }, {} as Record<string, number>)
  const senzaCoords = tutti.filter(wo => !wo.cliente_lat || !wo.cliente_lng)
  const conteggioStati = pratiche.reduce((acc, wo) => {
    acc[wo.stato] = (acc[wo.stato] || 0) + 1; return acc
  }, {} as Record<string, number>)

  // Attiva modalità giro: esattamente 1 tecnico selezionato + giorno singolo
  const giornoSingolo = !!(tecnicoFiltri.length === 1 && filtroDataDa &&
    (filtroDataA === '' || filtroDataA === filtroDataDa))

  useEffect(() => {
    if (giornoSingolo && pratiche.length > 0) {
      setModalitaGiro(true)
      const ordinata = [...pratiche].sort((a, b) => {
        if (a.ordine_giro != null && b.ordine_giro != null) return a.ordine_giro - b.ordine_giro
        return slotToKey(a.slot_orario).localeCompare(slotToKey(b.slot_orario))
      })
      setOrdineGiro(ordinata)
    } else {
      setModalitaGiro(false)
      setOrdineGiro([])
      setSidebarWO(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pratiche.length, tecnicoFiltri, filtroDataDa, filtroDataA])

  const handleMarkerClick = useCallback((wo: any) => {
    setSelectedId(wo.id)
    setSidebarWO(wo)
  }, [])

  // Inizializza mappa
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return
    const map = L.map(mapRef.current, { center: [41.9, 12.5], zoom: 6 })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map)
    mapInstanceRef.current = map
    markersLayerRef.current = L.layerGroup().addTo(map)
    return () => { map.remove(); mapInstanceRef.current = null }
  }, [])

  // Ridimensiona mappa quando la sidebar dettaglio appare/scompare
  useEffect(() => {
    const t = setTimeout(() => mapInstanceRef.current?.invalidateSize(), 50)
    return () => clearTimeout(t)
  }, [sidebarWO])

  // Aggiorna markers + polyline
  useEffect(() => {
    const layer = markersLayerRef.current
    const map = mapInstanceRef.current
    if (!layer || !map) return

    // Rimuovi polyline precedente
    if (polylineRef.current) { polylineRef.current.remove(); polylineRef.current = null }
    layer.clearLayers()

    if (modalitaGiro && ordineGiro.length > 0) {
      // MODALITÀ GIRO: markers numerati + polyline
      const bounds: [number, number][] = []
      ordineGiro.forEach((wo, idx) => {
        const lat = Number(wo.cliente_lat)
        const lng = Number(wo.cliente_lng)
        bounds.push([lat, lng])
        const marker = L.marker([lat, lng], {
          icon: creaIconaNumero(idx + 1, wo.stato, selectedId === wo.id),
        })
        marker.bindPopup(`
          <div style="min-width:190px;font-family:system-ui,sans-serif">
            <div style="font-size:12px;font-weight:700;color:#F97316;margin-bottom:2px">Tappa ${idx + 1}</div>
            <div style="font-family:monospace;font-size:12px;font-weight:700;color:#1E40AF;margin-bottom:4px">${wo.numero_progressivo}</div>
            <div style="font-size:13px;font-weight:600;color:#111827;margin-bottom:6px">${wo.denominazione_sito || wo.cliente_nome || ''}</div>
            <div style="font-size:11px;color:#6b7280;margin-bottom:4px">📍 ${wo.cliente_indirizzo || ''}, ${wo.cliente_comune || ''}</div>
            ${wo.slot_orario ? `<div style="font-size:11px;color:#1d4ed8;margin-bottom:6px">🕐 ${wo.slot_orario}</div>` : ''}
            <button onclick="window.__xsophia_nav('/pratiche/${wo.id}')" style="width:100%;padding:7px;background:#1E40AF;color:#fff;border:none;border-radius:7px;font-size:12px;font-weight:600;cursor:pointer">Apri →</button>
          </div>
        `)
        marker.on('click', () => handleMarkerClick(wo))
        layer.addLayer(marker)
      })

      if (bounds.length > 1) {
        polylineRef.current = L.polyline(bounds, {
          color: '#F97316', weight: 4, opacity: 0.85, dashArray: '10 5',
        }).addTo(map)
        map.fitBounds(L.latLngBounds(bounds), { padding: [50, 50] })
      } else if (bounds.length === 1) {
        map.setView(bounds[0], 13)
      }
    } else if (!modalitaGiro && pratiche.length > 0) {
      // MODALITÀ NORMALE: markers numerati — ordine_giro ha precedenza, poi cronologico
      const praticheSorted = [...pratiche].sort((a, b) => {
        const ao = a.ordine_giro, bo = b.ordine_giro
        if (ao != null && bo != null) return ao - bo
        if (ao != null) return -1
        if (bo != null) return 1
        const da = a.data_appuntamento || '9999-99-99'
        const db = b.data_appuntamento || '9999-99-99'
        if (da !== db) return da.localeCompare(db)
        return slotToKey(a.slot_orario).localeCompare(slotToKey(b.slot_orario))
      })
      const bounds: [number, number][] = []
      praticheSorted.forEach((wo, idx) => {
        const lat = Number(wo.cliente_lat)
        const lng = Number(wo.cliente_lng)
        bounds.push([lat, lng])
        const isLong  = wo.ora_chiusura != null && wo.ora_chiusura > '14:00'
        const isShort = wo.ora_chiusura?.startsWith('13:')
        const ringColor = wo.slot_orario?.trim().toUpperCase() === 'PM' || isLong
          ? '#22c55e'
          : isShort ? '#f59e0b' : undefined
        const markerNum = wo.ordine_giro ?? (idx + 1)
        const marker = L.marker([lat, lng], { icon: creaIconaNumero(markerNum, wo.stato, selectedId === wo.id, ringColor) })
        const dataStr = wo.data_appuntamento
          ? format(new Date(wo.data_appuntamento), 'dd/MM/yyyy', { locale: it })
          : ''
        const orariBadge = (wo.ora_apertura || wo.ora_chiusura)
          ? ` <span style="background:${isLong ? '#dcfce7' : isShort ? '#fef9c3' : '#f1f5f9'};color:${isLong ? '#15803d' : isShort ? '#92400e' : '#475569'};border-radius:4px;padding:1px 5px;font-size:10px;font-weight:700">🏛 ${wo.ora_apertura ?? '?'}–${wo.ora_chiusura ?? '?'}</span>`
          : ''
        marker.bindPopup(`
          <div style="min-width:200px;font-family:system-ui,sans-serif">
            <div style="font-size:12px;font-weight:700;color:#F97316;margin-bottom:2px">#${markerNum}${orariBadge}</div>
            <div style="font-family:monospace;font-size:13px;font-weight:700;color:#1E40AF;margin-bottom:4px">${wo.numero_progressivo}</div>
            <div style="font-size:13px;font-weight:600;color:#111827;margin-bottom:8px">${wo.denominazione_sito || wo.cliente_nome || ''}</div>
            <div style="font-size:12px;color:#6b7280;margin-bottom:4px">📍 ${wo.cliente_indirizzo || ''}<br>${wo.cliente_comune || ''} (${wo.cliente_provincia || ''})</div>
            <span style="display:inline-block;padding:2px 8px;border-radius:99px;font-size:11px;font-weight:600;background:${(STATO_COLORI[wo.stato] ?? '#6b7280')}22;color:${STATO_COLORI[wo.stato] ?? '#6b7280'};margin-bottom:8px">${STATO_LABEL[wo.stato] ?? wo.stato}</span>
            ${wo.tecnico_nome ? `<div style="font-size:12px;color:#374151;margin-bottom:6px">👷 ${wo.tecnico_nome}</div>` : ''}
            ${wo.slot_orario ? `<div style="font-size:11px;color:#1d4ed8;margin-bottom:4px">🕐 ${wo.slot_orario}</div>` : ''}
            ${dataStr ? `<div style="font-size:12px;color:#374151;margin-bottom:8px">📅 ${dataStr}</div>` : ''}
            <button onclick="window.__xsophia_nav('/pratiche/${wo.id}')" style="width:100%;padding:8px;background:#1E40AF;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer">Apri pratica →</button>
          </div>
        `)
        marker.on('click', () => handleMarkerClick(wo))
        layer.addLayer(marker)
      })

      // Polyline percorso se c'è almeno 2 WO con ordine_giro pianificato
      const conOrdine = praticheSorted.filter(
        wo => wo.ordine_giro != null && wo.cliente_lat != null && wo.cliente_lng != null
      )
      if (conOrdine.length > 1) {
        polylineRef.current = L.polyline(
          conOrdine.map(wo => [Number(wo.cliente_lat), Number(wo.cliente_lng)] as [number, number]),
          { color: '#1E40AF', weight: 3, opacity: 0.55, dashArray: '8 6' }
        ).addTo(map)
      }

      if (bounds.length === 1) map.setView(bounds[0], 13)
      else if (bounds.length > 1) map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40] })
    }
  // ordineGiro array reference cambia dopo ogni spostamento → aggiorna mappa
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pratiche.length, ordineGiro, modalitaGiro, statoFiltri, tecnicoFiltri, search, selectedId])

  useEffect(() => {
    (window as any).__xsophia_nav = (path: string) => navigate(path)
    return () => { delete (window as any).__xsophia_nav }
  }, [navigate])

  // Helpers ordine giro
  const spostaUp = (idx: number) => {
    if (idx === 0) return
    const n = [...ordineGiro];
    [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]
    setOrdineGiro(n)
  }
  const spostaDown = (idx: number) => {
    if (idx === ordineGiro.length - 1) return
    const n = [...ordineGiro];
    [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]
    setOrdineGiro(n)
  }
  const rimuoviDalGiro = (woId: number) => setOrdineGiro(prev => prev.filter(p => p.id !== woId))
  const aggiungiAlGiro = (wo: any) => {
    if (ordineGiro.find(p => p.id === wo.id)) return
    setOrdineGiro(prev => [...prev, wo])
    setShowAggiungiModal(false)
  }
  const pratiCheFuoriGiro = pratiche.filter(p => !ordineGiro.find(o => o.id === p.id))

  const tecnicoSelezionato = tecnicoFiltri.length === 1
    ? (tecniciData ?? []).find((t: any) => String(t.id) === tecnicoFiltri[0])
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0, position: 'relative', zIndex: 1200 }}>
        <h1 style={{ fontFamily: 'system-ui,sans-serif', fontSize: 17, fontWeight: 700, color: '#111827', margin: 0, whiteSpace: 'nowrap' }}>
          {modalitaGiro ? '🗺 Giro Tecnico' : '🗺 Mappa Pratiche'}
        </h1>

        <div style={{ position: 'relative', flex: '1 1 160px', maxWidth: 220 }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>🔍</span>
          <input
            style={{ width: '100%', padding: '7px 10px 7px 28px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            placeholder="Cerca pratica, comune..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>

        <MultiSelectDropdown
          widthClass="w-40"
          placeholder="Tutti gli stati"
          options={Object.entries(STATO_LABEL).map(([v, l]) => ({ value: v, label: l }))}
          selected={statoFiltri}
          onChange={(next) => { setStatoFiltri(next); setSelectedId(null) }}
        />

        {!isTecnico && (
          <MultiSelectDropdown
            widthClass="w-40"
            placeholder="Tutti i tecnici"
            options={(tecniciData ?? []).map((t: any) => ({ value: String(t.id), label: `${t.nome} ${t.cognome}` }))}
            selected={tecnicoFiltri}
            onChange={(next) => { setTecnicoFiltri(next); setSelectedId(null) }}
          />
        )}

        <select style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }} value={filtroCommittente} onChange={e => { setFiltroCommittente(e.target.value); setSelectedId(null) }}>
          <option value="">Tutti i committenti</option>
          {committenti.map((c: any) => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
        </select>

        <select style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }} value={filtroRegione} onChange={e => { setFiltroRegione(e.target.value); setSelectedId(null) }}>
          <option value="">Tutte le regioni</option>
          {regioniDisponibili.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }} value={filtroLotto} onChange={e => { setFiltroLotto(e.target.value); setSelectedId(null) }}>
          <option value="">Tutti i lotti</option>
          {lottiDisponibili.map(l => <option key={l} value={l}>{l}</option>)}
        </select>

        {/* Range date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="date" value={filtroDataDa}
            onChange={e => setFiltroDataDa(e.target.value)}
            style={{ padding: '6px 8px', border: `1px solid ${filtroDataDa ? '#F97316' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12 }}
          />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>→</span>
          <input type="date" value={filtroDataA}
            onChange={e => setFiltroDataA(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
          />
          {(filtroDataDa || filtroDataA) && (
            <button onClick={() => { setFiltroDataDa(''); setFiltroDataA('') }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '0 2px' }}
              title="Cancella date">✕</button>
          )}
        </div>

        {/* Badge modalità giro */}
        {modalitaGiro && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700, background: '#fff7ed', color: '#F97316', border: '1px solid #fed7aa' }}>
            🚗 Giro attivo · {ordineGiro.length} siti
          </span>
        )}

        {/* Legenda stati + priorità (solo modalità normale) */}
        {!modalitaGiro && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }}>
            {Object.entries(STATO_COLORI).map(([stato, colore]) => {
              const attivo = statoFiltri.includes(stato)
              return (
                <span key={stato} onClick={() => setStatoFiltri(prev => prev.includes(stato) ? prev.filter(s => s !== stato) : [...prev, stato])}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: attivo ? colore : colore + '20', color: attivo ? '#fff' : colore, border: `1px solid ${colore}40` }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: colore }} />
                  {STATO_LABEL[stato]}{conteggioStati[stato] ? ` (${conteggioStati[stato]})` : ''}
                </span>
              )
            })}
            <span style={{ width: 1, height: 18, background: '#e5e7eb', margin: '0 2px' }} />
            {Object.entries(PRIORITA_COLORI).map(([p, colore]) => conteggioP[p] ? (
              <span key={p} onClick={() => setFiltroPriorita(filtroPriorita === p ? '' : p)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: filtroPriorita === p ? colore : colore + '20', color: filtroPriorita === p ? '#fff' : colore, border: `1px solid ${colore}40` }}>
                ⚡ {p} ({conteggioP[p]})
              </span>
            ) : null)}
          </div>
        )}

        <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {isLoading ? 'Caricamento...' : `${pratiche.length} su mappa`}
          {senzaCoords.length > 0 && <span style={{ color: '#f59e0b', marginLeft: 6 }}>· {senzaCoords.length} senza coordinate</span>}
        </span>
        <button onClick={() => refetch()} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>↻</button>
      </div>

      {/* Hint giro */}
      {!modalitaGiro && tecnicoFiltri.length === 1 && filtroDataDa && (
        <div style={{ background: '#fff7ed', borderBottom: '1px solid #fed7aa', padding: '6px 16px', fontSize: 12, color: '#c2410c' }}>
          💡 Seleziona un solo giorno (Da = A) per attivare la modalità Giro Tecnico con percorso ordinato
        </div>
      )}

      {/* Mappa + pannello */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {isLoading && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 1000, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #dbeafe', borderTopColor: '#1E40AF', animation: 'spin .7s linear infinite' }} />
              <span style={{ fontSize: 14, color: '#6b7280' }}>Caricamento...</span>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>

        {/* Pannello giro tecnico */}
        {modalitaGiro && (
          <div style={{ width: 310, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header pannello */}
            <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF', marginBottom: 2 }}>
                🗺 Giro del {filtroDataDa ? format(new Date(filtroDataDa), 'dd/MM/yyyy') : '—'}
              </div>
              {tecnicoSelezionato && (
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                  👷 {tecnicoSelezionato.nome} {tecnicoSelezionato.cognome}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                {ordineGiro.length} siti · trascina ↑↓ per riordinare
              </div>
            </div>

            {/* Lista ordinata */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {ordineGiro.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  Nessun sito con coordinate nel giro
                </div>
              ) : ordineGiro.map((wo, idx) => (
                <div key={wo.id}
                  onClick={() => { setSelectedId(wo.id); setSidebarWO(null) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                    borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                    background: selectedId === wo.id ? '#eff6ff' : (wo.ora_chiusura && wo.ora_chiusura > '14:00' ? '#f0fff4' : 'transparent'),
                  }}
                >
                  {/* Numero */}
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: STATO_COLORI[wo.stato] ?? '#6b7280',
                    color: '#fff', fontSize: 12, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{idx + 1}</div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: '#1E40AF' }}>
                      {wo.numero_progressivo}
                    </div>
                    <div style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {wo.cliente_comune || wo.cliente_indirizzo || '—'}
                    </div>
                    {(wo.ora_apertura || wo.ora_chiusura) && <AppuntamentoChip wo={wo} />}
                  </div>

                  {/* Controlli */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); spostaUp(idx) }}
                      disabled={idx === 0}
                      style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, cursor: idx === 0 ? 'default' : 'pointer', padding: '1px 5px', fontSize: 10, color: idx === 0 ? '#d1d5db' : '#374151' }}>▲</button>
                    <button onClick={e => { e.stopPropagation(); spostaDown(idx) }}
                      disabled={idx === ordineGiro.length - 1}
                      style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, cursor: idx === ordineGiro.length - 1 ? 'default' : 'pointer', padding: '1px 5px', fontSize: 10, color: idx === ordineGiro.length - 1 ? '#d1d5db' : '#374151' }}>▼</button>
                  </div>

                  {/* Rimuovi */}
                  <button onClick={e => { e.stopPropagation(); rimuoviDalGiro(wo.id) }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14, padding: '0 2px', flexShrink: 0 }}
                    title="Rimuovi dal giro">✕</button>
                </div>
              ))}
            </div>

            {/* Footer azioni */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
              {pratiCheFuoriGiro.length > 0 && (
                <button
                  onClick={() => setShowAggiungiModal(true)}
                  style={{ padding: '8px', borderRadius: 8, border: '1px dashed #d1d5db', background: '#f9fafb', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  + Aggiungi sito ({pratiCheFuoriGiro.length} disponibili)
                </button>
              )}
              <button
                onClick={() => salvaOrdineMut.mutate(ordineGiro.map((wo, idx) => ({ wo_id: wo.id, ordine: idx + 1 })))}
                disabled={salvaOrdineMut.isPending || ordineGiro.length === 0}
                style={{ padding: '9px', borderRadius: 8, background: '#1E40AF', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: salvaOrdineMut.isPending ? 0.7 : 1 }}>
                {salvaOrdineMut.isPending ? 'Salvataggio...' : '💾 Salva ordine giro'}
              </button>
            </div>
          </div>
        )}

        {/* Sidebar dettaglio WO (modalità normale) */}
        {!modalitaGiro && sidebarWO && (
          <div style={{ width: 290, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e5e7eb', overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#1E40AF' }}>{sidebarWO.numero_progressivo}</span>
              <button onClick={() => { setSidebarWO(null); setSelectedId(null) }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>✕</button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{sidebarWO.denominazione_sito || sidebarWO.cliente_nome}</div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: (STATO_COLORI[sidebarWO.stato] ?? '#6b7280') + '20', color: STATO_COLORI[sidebarWO.stato] ?? '#6b7280', width: 'fit-content' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATO_COLORI[sidebarWO.stato] ?? '#6b7280' }} />
              {STATO_LABEL[sidebarWO.stato] ?? sidebarWO.stato}
            </span>
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
              {[
                ['Committente', sidebarWO.committente_nome],
                ['Indirizzo', sidebarWO.cliente_indirizzo],
                ['Comune', sidebarWO.cliente_comune],
                ['Provincia', sidebarWO.cliente_provincia],
                ['Regione', sidebarWO.cliente_regione],
                ['Tecnico', sidebarWO.tecnico_nome || '—'],
                ['Tipologia', sidebarWO.tipologia?.replace(/_/g, ' ')],
                ['Appuntamento', sidebarWO.data_appuntamento ? format(new Date(sidebarWO.data_appuntamento), 'dd/MM/yyyy', { locale: it }) : '—'],
                ['Ora', sidebarWO.ore_appuntamento || sidebarWO.slot_orario || '—'],
              ].map(([label, val]) => val && (
                <div key={label as string} style={{ marginBottom: 7 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 1 }}>{val}</div>
                </div>
              ))}
            </div>
            {sidebarWO.cliente_indirizzo && (
              <a href={sidebarWO.cliente_lat && sidebarWO.cliente_lng
                ? `https://www.google.com/maps?q=${sidebarWO.cliente_lat},${sidebarWO.cliente_lng}`
                : `https://www.google.com/maps?q=${encodeURIComponent(`${sidebarWO.cliente_indirizzo}, ${sidebarWO.cliente_comune}`)}`} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10, borderRadius: 10, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                🧭 Apri in Google Maps
              </a>
            )}
            <button onClick={() => navigate(`/pratiche/${sidebarWO.id}`)}
              style={{ padding: 12, borderRadius: 10, background: '#1E40AF', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Apri pratica completa →
            </button>
          </div>
        )}
      </div>

      {/* Modale aggiungi sito al giro */}
      {showAggiungiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowAggiungiModal(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 360, maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Aggiungi sito al giro</span>
              <button onClick={() => setShowAggiungiModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {pratiCheFuoriGiro.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Tutti i siti sono già nel giro</div>
              ) : pratiCheFuoriGiro.map(wo => (
                <div key={wo.id}
                  onClick={() => aggiungiAlGiro(wo)}
                  style={{ padding: '10px 18px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: '#1E40AF' }}>{wo.numero_progressivo}</div>
                    <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {wo.cliente_comune || wo.cliente_indirizzo}
                    </div>
                    {wo.slot_orario && <div style={{ fontSize: 10, color: '#1d4ed8' }}>🕐 {wo.slot_orario}</div>}
                  </div>
                  <span style={{ fontSize: 18, color: '#22c55e' }}>+</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
